import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { TrafficLogEntry } from "../lib/proximityAlarm";
import { exportLogbookEntryPdf } from "../lib/pdfExport";

type Profile = { name: string; pilotId: string; operatorId: string; photoUrl?: string | null };

type LogbookEntry = {
  id: string;
  droneName: string;
  droneClass: string;
  flightDate: string;
  locationName: string | null;
  takeoffTime: string | null;
  landingTime: string | null;
  flightDurationMinutes: number | null;
  maxAltitude: number | null;
  incidentOccurred: boolean;
  notes: string | null;
  trafficLog: TrafficLogEntry[] | null;
};

type TrafficFilter = "all" | "warning" | "critical";

export default function Logbook() {
  const { data: entries = [], isLoading } = useQuery<LogbookEntry[]>({ queryKey: ["/api/logbook"] });
  const { data: profile } = useQuery<Profile | null>({ queryKey: ["/api/profile"] });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2">
        <Link to="/logbuch/dji-bulk" className="btn-secondary inline-flex text-sm" data-testid="link-dji-bulk">
          Mehrere Logs importieren
        </Link>
        <Link to="/logbuch/dji-auswertung" className="btn-secondary inline-flex text-sm" data-testid="link-dji-analyze">
          DJI-Log auswerten
        </Link>
      </div>
      {isLoading ? (
        <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>
      ) : entries.length === 0 ? (
        <div className="card text-center py-12 space-y-3">
          <p className="text-[var(--color-app-text-secondary)]">Noch keine Logbucheinträge.</p>
          <p className="text-xs text-[var(--color-app-text-tertiary)]">
            Beim Abschluss eines Fluges wird automatisch ein Eintrag erstellt.
          </p>
          <Link to="/fluege" className="btn-primary inline-flex">
            Flug starten
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => {
            const log = Array.isArray(e.trafficLog) ? e.trafficLog : [];
            const isOpen = expandedId === e.id;
            const hasLog = log.length > 0;
            const meta = [
              e.flightDate,
              e.locationName || null,
              e.flightDurationMinutes ? `${e.flightDurationMinutes} min` : null,
              e.maxAltitude ? `${e.maxAltitude} m` : null,
            ].filter(Boolean) as string[];
            return (
              <div key={e.id} className="card space-y-3" data-testid={`card-logbook-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[var(--color-app-text)] truncate">
                      {e.droneName}
                    </div>
                    {e.droneClass ? (
                      <div className="text-xs text-[var(--color-app-text-secondary)] mt-0.5">
                        {e.droneClass}
                      </div>
                    ) : null}
                  </div>
                  {e.incidentOccurred ? <span className="pill-nogo">Vorfall</span> : null}
                </div>

                <div className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-[var(--color-app-text-secondary)]">
                  {meta.map((m, i) => (
                    <Fragment key={i}>
                      {i > 0 ? <span className="text-[var(--color-app-text-tertiary)]">·</span> : null}
                      <span>{m}</span>
                    </Fragment>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3 pt-1">
                  <div>
                    {hasLog ? (
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : e.id)}
                        className="ios-pill"
                        style={{
                          background: "var(--color-app-bg-tertiary)",
                          color: "var(--color-app-text-secondary)",
                        }}
                        data-testid={`button-traffic-${e.id}`}
                      >
                        {isOpen ? "Verkehr ausblenden" : `Verkehr (${log.length})`}
                      </button>
                    ) : null}
                  </div>
                  <div className="flex gap-4 whitespace-nowrap">
                    <Link
                      to={`/logbuch/${e.id}`}
                      className="text-sm text-brand-600 hover:underline"
                    >
                      Detail
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        exportLogbookEntryPdf({
                          ...e,
                          pilotName: profile?.name || null,
                          pilotId: profile?.pilotId || null,
                          operatorId: profile?.operatorId || null,
                          pilotPhotoUrl: profile?.photoUrl || null,
                        })
                      }
                      className="text-sm text-brand-600 hover:underline"
                    >
                      PDF
                    </button>
                  </div>
                </div>

                {isOpen && hasLog ? (
                  <div className="pt-1 border-t border-[var(--color-app-border)]">
                    <div className="pt-3">
                      <TrafficLogDetail log={log} notes={e.notes} />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrafficLogDetail({ log, notes }: { log: TrafficLogEntry[]; notes: string | null }) {
  const [filter, setFilter] = useState<TrafficFilter>("all");
  const filtered = log.filter((e) => filter === "all" || e.level === filter);
  const sorted = [...filtered].sort((a, b) => a.ts - b.ts);
  const criticalCount = log.filter((e) => e.level === "critical").length;
  const warnCount = log.filter((e) => e.level === "warning").length;

  return (
    <div className="space-y-3">
      {notes ? (
        <div className="text-sm text-[var(--color-app-text)]">
          <span className="font-medium text-[var(--color-app-text-secondary)]">Notizen: </span>
          {notes}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm font-semibold text-[var(--color-app-text)]">
          Verkehrs-Annäherungen ({log.length})
          <span className="ml-2 text-xs font-normal text-[var(--color-app-text-secondary)]">
            {criticalCount} kritisch · {warnCount} Warnung
          </span>
        </div>
        <div className="inline-flex rounded-md border border-[var(--color-app-border)] overflow-hidden text-xs">
          {(["all", "critical", "warning"] as TrafficFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 ${
                filter === f
                  ? "bg-brand-500 text-white"
                  : "bg-white text-[var(--color-app-text-secondary)] hover:bg-[var(--color-app-bg-secondary)]"
              }`}
              aria-pressed={filter === f}
            >
              {f === "all" ? "Alle" : f === "critical" ? "Kritisch" : "Warnung"}
            </button>
          ))}
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-[var(--color-app-text-secondary)] py-3 text-center">
          Keine Annäherungen für diesen Filter.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[var(--color-app-border)] bg-white">
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-app-bg-secondary)] text-left text-[11px] uppercase text-[var(--color-app-text-secondary)]">
              <tr>
                <th className="px-3 py-2">Zeit</th>
                <th className="px-3 py-2">Stufe</th>
                <th className="px-3 py-2">Kennung</th>
                <th className="px-3 py-2">Distanz</th>
                <th className="px-3 py-2">Höhe</th>
                <th className="px-3 py-2">Richtung</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-app-border)]">
              {sorted.map((t, i) => (
                <tr key={`${t.ts}-${t.icao24}-${i}`}>
                  <td className="px-3 py-2 text-[var(--color-app-text)] whitespace-nowrap">
                    {new Date(t.ts).toLocaleString("de-DE")}
                  </td>
                  <td className="px-3 py-2">
                    <span className={t.level === "critical" ? "pill-nogo" : "pill-caution"}>
                      <span
                        className="size-1.5 rounded-full mr-1"
                        style={{
                          background:
                            t.level === "critical"
                              ? "var(--color-nogo)"
                              : "var(--color-caution)",
                        }}
                      />
                      {t.level === "critical" ? "Kritisch" : "Warnung"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-[var(--color-app-text)]">{t.callsign}</td>
                  <td className="px-3 py-2 text-[var(--color-app-text)]">
                    {t.distanceKm < 1
                      ? `${Math.round(t.distanceKm * 1000)} m`
                      : `${t.distanceKm.toFixed(1)} km`}
                  </td>
                  <td className="px-3 py-2 text-[var(--color-app-text)]">
                    {t.altitudeM != null ? `${Math.round(t.altitudeM)} m` : "—"}
                  </td>
                  <td className="px-3 py-2 text-[var(--color-app-text)]">
                    {t.direction} ({Math.round(t.bearingDeg)}°)
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
